import { useIntl } from 'react-intl';

import { EM_DASH } from '~/common/constants/unicode';
import { Box } from '~/eds/src/components/Box/Box';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Text } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { DataFieldType } from '~/enums/DataFieldType';
import { DateFormat } from '~/i18n/hooks/useFormatDate/utils';
import { getUniqueFieldValues } from '~/utils/document.utils';

import { EditData, OverviewField } from '../types';
import { AttachmentField } from './fields/AttachmentField';
import { BooleanField } from './fields/BooleanField';
import { DateField } from './fields/DateField';
import { MultipleArrayField } from './fields/MultipleArrayField';
import { NumberField } from './fields/NumberField';
import { SingleArrayField } from './fields/SingleArrayField';
import { StringField } from './fields/StringField';
import { TextAreaField } from './fields/TextAreaField';

interface FieldRendererProps {
  field: OverviewField;
  editMode: boolean;
  editData: EditData;
  documentId: number;
  showAllParties: boolean;
  isDownloadDocumentAttachmentEnabled: boolean;
  onEditDataChange: (fieldId: number, value: any) => void;
  onDateChange: (
    date: Date | null,
    fieldId: number,
    format: DateFormat,
  ) => void;
  onFileAttachError: () => void;
  onShowAllParties: () => void;
  isWssField: (field: OverviewField) => boolean;
}

export const FieldRenderer = ({
  field,
  editMode,
  editData,
  documentId,
  showAllParties,
  isDownloadDocumentAttachmentEnabled,
  onEditDataChange,
  onDateChange,
  onFileAttachError,
  onShowAllParties,
  isWssField,
}: FieldRendererProps) => {
  const intl = useIntl();

  const renderItemValue = () => {
    const baseProps = {
      field,
      editMode,
      editData,
      onEditDataChange,
      isWssField,
    };

    switch (field.type) {
      case DataFieldType.BOOLEAN:
        return <BooleanField {...baseProps} />;

      case DataFieldType.DATE:
        return <DateField {...baseProps} onDateChange={onDateChange} />;

      case DataFieldType.NUMBER:
        return <NumberField {...baseProps} />;

      case DataFieldType.REFERENCE:
      case DataFieldType.STRING:
        if (field.static_key_name === 'contract_description') {
          return <TextAreaField {...baseProps} />;
        }
        return <StringField {...baseProps} />;

      case DataFieldType.TEXT_AREA:
        return <TextAreaField {...baseProps} />;

      case DataFieldType.ARRAY_SINGLE:
        return <SingleArrayField {...baseProps} />;

      case DataFieldType.ARRAY_MULTIPLE:
        return (
          <MultipleArrayField
            {...baseProps}
            field={{
              ...field,
              value: getUniqueFieldValues(
                Array.isArray(field.value) ? field.value : [],
              ),
            }}
            showAllParties={showAllParties}
            onShowAllParties={onShowAllParties}
          />
        );

      case DataFieldType.ATTACHMENT:
        return (
          <AttachmentField
            {...baseProps}
            documentId={documentId}
            isDownloadDocumentAttachmentEnabled={
              isDownloadDocumentAttachmentEnabled
            }
            onFileAttachError={onFileAttachError}
          />
        );

      default:
        return EM_DASH;
    }
  };

  const isContractDescription =
    field.static_key_name === 'contract_description';
  const isTextAreaLike =
    field.type === DataFieldType.TEXT_AREA || isContractDescription;
  const isWideField =
    editMode && (field.type === DataFieldType.ARRAY_MULTIPLE || isTextAreaLike);
  const isAttachmentField = field.type === DataFieldType.ATTACHMENT;
  const shouldWrapAfter = isContractDescription && editMode;

  const getFieldWidth = () => {
    if (isAttachmentField) return '75%';
    if (shouldWrapAfter) return '100%';
    if (isWideField) return '50%';
    if (editMode) return 'auto';
    return '25%';
  };

  const getValueMaxWidth = () => {
    if (shouldWrapAfter) return 'calc(50% - 8px)';
    return undefined;
  };

  return (
    <Box
      key={`overviewItem-${field.name}`}
      mb={8}
      pr={4}
      style={{
        width: getFieldWidth(),
        minWidth: '25%',
      }}
    >
      <Text
        as="div"
        variant="tiny"
        color="text.quiet"
        style={{
          marginBottom: '8px',
          display: 'inline-flex',
          textTransform: 'uppercase',
          alignItems: 'center',
        }}
      >
        {field.name}

        {field.help_text && (
          <Icon
            size="s"
            icon="info"
            id={`helpTextTag-${field.id}`}
            tooltip={field.help_text}
            placement="bottom"
          />
        )}
        {isWssField(field) && (
          <Icon
            icon="database"
            tooltip={intl.formatMessage({
              id: 'FieldRenderer.strategicSourcingTooltip',
              defaultMessage: 'Source: Strategic Sourcing',
              description: 'Tooltip for strategic sourcing fields',
            })}
          />
        )}
        {field.is_beta && (
          <Box ml={1}>
            <Tooltip
              placement="bottom"
              tooltip={intl.formatMessage({
                id: 'FieldRenderer.betaTooltip',
                defaultMessage:
                  'This is a new intelligent field. Contact beta@evisort.com with feedback and suggestions',
                description: 'Tooltip text for beta fields',
              })}
            >
              <Chip
                mode="tag"
                preset="beta"
                text={intl.formatMessage({
                  id: 'DocumentOverviewPanel.betaLabel',
                  defaultMessage: 'BETA',
                  description: 'Beta label for new fields',
                })}
              />
            </Tooltip>
          </Box>
        )}
      </Text>
      <Text
        as="div"
        variant="body-medium"
        color="text.primary"
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          maxWidth: getValueMaxWidth(),
        }}
      >
        {renderItemValue()}
      </Text>
    </Box>
  );
};
